import { Timestamp } from 'firebase/firestore';
import type { DocListConfig } from '../DocList.types';
import type {
    FormVersionWithPath,
    ListFormVersionsDocsRequest,
    ListFormVersionsDocsResponse
} from '#types';
import { homePageConfig, AgentType } from '#types';
import { MetaFieldUIDescription, formStateDescription } from '#types/System.Parameters.types';

const primaryEntityKey = homePageConfig.formsTable.primaryEntityReferenceKey;

export const formVersionsConfig: DocListConfig<FormVersionWithPath> = {
    configId: 'formVersions',
    viewSettingsId: 'formListViewSettings',
    documentLabel: { singular: 'form', plural: 'forms' },
    requiredColumnIds: ['form'],
    enableActionsColumn: false,
    
    dataSource: {
        functionName: 'listFormVersionsDocs',
        
        buildRequest: (params, cursor, limit = 1000): ListFormVersionsDocsRequest => {
            const now = new Date();
            const startOfYear = new Date(now.getFullYear(), 0, 1);
            
            return {
                startDate: params.startDate
                    ? (params.startDate as any)
                    : { _seconds: Math.floor(startOfYear.getTime() / 1000), _nanoseconds: 0 },
                endDate: params.endDate
                    ? (params.endDate as any)
                    : { _seconds: Math.floor(now.getTime() / 1000), _nanoseconds: 0 },
                limit,
                includeCounts: true,
                ...(cursor ? { cursor } : {}),
                ...(params.where ? { where: params.where as ListFormVersionsDocsRequest['where'] } : {}),
                ...(params.orderBy ? { orderBy: params.orderBy as ListFormVersionsDocsRequest['orderBy'] } : {}),
            };
        },
        
        parseResponse: (data: ListFormVersionsDocsResponse) => ({
            documents: data.documents,
            batchCount: data.batchCount,
            queryTotalCount: data.queryTotalCount,
            nextCursor: data.nextCursor,
            hasMore: data.hasMore,
            aggregateResult: data.aggregateResult,
        }),
    },
    
    columns: [
        {
            id: 'form',
            header: 'Form',
            accessor: (row) => {
                const formNumber = row.parentFormNumber ?? row.parentDenormalizedData?.formNumber ?? '';
                const version = row.formVersionNumber ?? 0;
                return formNumber ? `${formNumber} v${version}` : `Form v${version}`;
            },
            filterable: true,
            serverFilterField: 'parentFormNumber',
            serverSortField: 'parentFormNumber',
            link: {
                getPath: (row) => row._documentPath,
            },
            size: 175,
        },
        {
            id: 'formType',
            header: 'Type',
            accessor: (row) => row.parentFormType ?? row.parentDenormalizedData?.formType ?? '',
            filterable: true,
            serverFilterField: 'parentFormType',
            serverSortField: 'parentFormType',
            size: 150,
        },
        {
            id: 'formState',
            header: 'State',
            accessor: (row) => row.state ?? '',
            filterable: true,
            serverFilterField: 'state',
            serverSortField: 'state',
            getFilterOptions: () => Object.entries(formStateDescription).map(([value, desc]) => ({ label: desc.shortLabel, value })),
            size: 140,
        },
        {
            id: 'assignedTo',
            header: 'Assigned',
            accessor: (row) => row.parentDenormalizedData?.assignedTo ?? '',
            filterable: true,
            serverFilterField: 'parentDenormalizedData.assignedTo',
            serverSortField: 'parentDenormalizedData.assignedTo',
            size: 200,
        },
        {
            id: 'subscribed',
            header: 'Subscribed',
            accessor: (row) => {
                const subscribers = row.parentDenormalizedData?.notificationsSubscribers ?? [];
                return Array.isArray(subscribers) ? subscribers : [];
            },
            filterable: true,
            serverFilterField: 'parentDenormalizedData.notificationsSubscribers',
            serverFilterOp: 'array-contains-any',
            size: 200,
        },
        {
            id: 'formNumber',
            header: 'Form Number',
            accessor: (row) => row.parentFormNumber ?? row.parentDenormalizedData?.formNumber ?? '',
            size: 150,
            hiddenByDefault: true,
        },
        {
            id: 'formVersionNumber',
            header: 'Form Version Number',
            accessor: (row) => row.formVersionNumber ?? '',
            size: 180,
            hiddenByDefault: true,
        },
        {
            id: 'modifiedDate',
            header: 'Modified date',
            serverSortField: 'meta.lastModified',
            accessor: (row) => {
                const timestamp = row.meta?.lastModified;
                if (!timestamp) return '';

                if (timestamp instanceof Timestamp) {
                    return timestamp.toDate();
                }

                if (typeof timestamp === 'object' && '_seconds' in timestamp) {
                    return new Date(
                        (timestamp as any)._seconds * 1000 + ((timestamp as any)._nanoseconds || 0) / 1000000
                    );
                }

                return timestamp;
            },
            render: (value) => {
                if (value instanceof Date) {
                    return value.toLocaleDateString();
                }
                if (typeof value === 'string') {
                    return value;
                }
                return '';
            },
            size: 160,
        },
        {
            id: 'lastModified',
            header: MetaFieldUIDescription.lastModified.shortLabel,
            accessor: (row) => {
                const timestamp = row.meta?.lastModified;
                if (!timestamp) return '';

                if (timestamp instanceof Timestamp) {
                    return timestamp.toDate();
                }

                if (typeof timestamp === 'object' && '_seconds' in timestamp) {
                    return new Date(
                        (timestamp as any)._seconds * 1000 + ((timestamp as any)._nanoseconds || 0) / 1000000
                    );
                }

                return timestamp;
            },
            size: 220,
            hiddenByDefault: true,
        },
        {
            id: 'created',
            header: MetaFieldUIDescription.created.shortLabel,
            accessor: (row) => {
                const timestamp = row.meta?.created;
                if (!timestamp) return '';

                if (timestamp instanceof Timestamp) {
                    return timestamp.toDate();
                }

                if (typeof timestamp === 'object' && '_seconds' in timestamp) {
                    return new Date(
                        (timestamp as any)._seconds * 1000 + ((timestamp as any)._nanoseconds || 0) / 1000000
                    );
                }

                return timestamp;
            },
            size: 220,
            hiddenByDefault: true,
        },
        {
            id: 'modifiedBy',
            header: MetaFieldUIDescription.userId.shortLabel,
            accessor: (row) => row.meta?.userId ?? '',
            filterable: true,
            serverFilterField: 'meta.userId',
            size: 200,
            hiddenByDefault: true,
        },
        {
            id: 'agentType',
            header: MetaFieldUIDescription.agentType.shortLabel,
            accessor: (row) => row.meta?.agentType ?? '',
            filterable: true,
            serverFilterField: 'meta.agentType',
            getFilterOptions: () => Object.values(AgentType).map((value) => ({ label: value, value })),
            size: 200,
            hiddenByDefault: true,
        },
    ],
    
    queryControls: [],
    
    defaultQueryParams: {},
    
    defaultSorting: [{ id: 'modifiedDate', desc: true }],

    allowedFilterCombinations: [
        ['formState'],
        ['formType'],
        ['assignedTo'],
        ['modifiedBy'],
        ['agentType'],
        ['subscribed'],
        ['form'],
        ...(primaryEntityKey ? [[primaryEntityKey]] : []),
        ['formType', 'formState'],
        ['assignedTo', 'formState'],
        ['assignedTo', 'formState', 'formType'],
        ['subscribed', 'formType'],
        ['subscribed', 'assignedTo'],
        ['subscribed', 'form'],
        ['subscribed', 'formState'],
    ],

    rowHeight: 40,
};

